import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { instituteListInfiniteQueryOptions } from '../api/institutes.api';
import { useInstituteListFilters } from '../hooks/useInstituteListFilters';
import type { InstituteListItem } from '../types/institute.types';
import { getInstituteColumns } from './InstituteColumns';
import { InstituteDeleteConfirmation } from './InstituteDeleteConfirmation';
import { InstituteFilterPanel } from './InstituteFilterPanel';

export function InstituteListTable() {
  const navigate = useNavigate();
  const controller = useInstituteListFilters();
  const { appliedSearchText, queryParams, pendingId, clearPendingId } = controller;
  const hasRequiredFilters = queryParams !== null;

  const [isFilterOpen, setIsFilterOpen] = useState(!hasRequiredFilters);
  const [instituteToDelete, setInstituteToDelete] = useState<InstituteListItem | null>(null);

  const { rows, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(instituteListInfiniteQueryOptions(queryParams));
  const isReloadingList = isLoading || isRefetching;

  useEffect(() => {
    if (pendingId !== undefined && !isReloadingList) {
      clearPendingId();
    }
  }, [pendingId, isReloadingList, clearPendingId]);

  const goToCreateInstitute = useCallback(() => {
    void navigate({ to: '/settings/institutes/create' });
  }, [navigate]);

  const goToEditInstitute = useCallback(
    (institute: InstituteListItem) => {
      void navigate({
        to: '/settings/institutes/$instituteId/edit',
        params: { instituteId: String(institute.id) },
        search: (prev) => prev,
      });
    },
    [navigate],
  );

  const columns = useMemo(
    () =>
      getInstituteColumns(appliedSearchText, {
        onEdit: goToEditInstitute,
        onDelete: setInstituteToDelete,
      }),
    [appliedSearchText, goToEditInstitute],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Institutes"
          search={{
            value: controller.searchText,
            onChange: controller.setSearchText,
            placeholder: 'Search by name',
          }}
          actions={
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
          }
          createAction={{ label: 'Add Institute', onClick: goToCreateInstitute }}
        />

        <FilterPanelCollapse isOpen={isFilterOpen}>
          <InstituteFilterPanel filters={controller} />
        </FilterPanelCollapse>

        {!hasRequiredFilters ? (
          <EmptyState
            kind="pickFirst"
            title="Select filters to view institutes"
            description="Choose an institute type and a district in Filters."
            className="lg:flex-1"
          />
        ) : loadError ? (
          <ErrorState
            error={loadError}
            fallbackTitle="Could not load institutes"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="name"
            className="lg:flex-1"
            isRowHighlighted={(row) => row.id === pendingId}
            getRowClassName={(row) =>
              isReloadingList && pendingId !== undefined && row.id === pendingId
                ? 'animate-pulse opacity-50 pointer-events-none'
                : undefined
            }
            {...loadMoreProps}
          />
        )}
      </div>

      <InstituteDeleteConfirmation
        institute={instituteToDelete}
        open={instituteToDelete !== null}
        onOpenChange={(open) => !open && setInstituteToDelete(null)}
      />
    </>
  );
}
